/**
 * <product-gallery>
 * ---------------------------------------------------------------------------
 * Galería de producto con filtrado por color.
 *
 * Escucha `variant:change` (emitido por <variant-picker>) y muestra solo las
 * imágenes del color elegido, manteniendo visibles las imágenes fijas — las que
 * no pertenecen a ningún color.
 *
 * El servidor ya entrega el HTML filtrado para la variante inicial, así que no
 * hay parpadeo al cargar. Este componente solo se ocupa de los cambios.
 *
 * Convención de agrupación (ver snippets/product-media-gallery.liquid):
 *   data-color-group=""            → imagen fija, visible siempre
 *   data-color-group="Azul marino" → visible solo con ese color
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.thumbsContainer = this.querySelector('[data-gallery-thumbs]');
    this.filteringEnabled = this.dataset.filtering === 'true';

    this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    document.addEventListener('variant:change', this.#onVariantChange);

    this.thumbsContainer?.addEventListener('click', this.#onThumbClick);
    this.stage?.addEventListener('scroll', this.#onStageScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.#onVariantChange);
  }

  #onVariantChange = (event) => {
    if (!this.filteringEnabled) return;

    // El evento sube hasta `document`, y en una ficha de producto hay más de un
    // selector de variantes en la página: el de la ficha y el del modal de
    // compra rápida que se abre desde los productos relacionados. Sin este
    // filtro, elegir "Verde" en el modal de otro producto refiltraba esta
    // galería.
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    const { colorValue } = event.detail;
    if (typeof colorValue !== 'string') return;
    if (colorValue === this.dataset.selectedColor) return;

    this.filterByColor(colorValue);
  };

  /**
   * Muestra las imágenes del color indicado más las imágenes fijas.
   * Si ese color no tiene ninguna imagen propia, no se oculta nada: es preferible
   * enseñar el producto entero a dejar la galería vacía.
   */
  filterByColor(colorValue) {
    const hasOwnMedia = this.items.some((item) => item.dataset.colorGroup === colorValue);

    const matches = (element) => {
      if (!hasOwnMedia) return true;
      const group = element.dataset.colorGroup;
      return group === '' || group === colorValue;
    };

    this.items.forEach((item) => {
      item.hidden = !matches(item);
    });

    this.thumbs.forEach((thumb) => {
      thumb.hidden = !matches(thumb);
    });

    this.dataset.selectedColor = colorValue;

    // Vuelve al principio para que la primera foto del nuevo color quede a la vista
    const firstVisible = this.items.find((item) => !item.hidden);
    if (firstVisible) this.#scrollTo(firstVisible, { instant: true });

    this.dispatchEvent(
      new CustomEvent('gallery:filtered', {
        bubbles: true,
        detail: { colorValue, visibleCount: this.items.filter((item) => !item.hidden).length },
      })
    );
  }

  #onThumbClick = (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb) return;

    const target = this.items.find((item) => item.dataset.mediaId === thumb.dataset.mediaId);
    if (target) this.#scrollTo(target);
  };

  #scrollTo(item, { instant = false } = {}) {
    this.stage?.scrollTo({
      left: item.offsetLeft - this.stage.offsetLeft,
      behavior: instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
    this.#setActiveThumb(item.dataset.mediaId);
  }

  /** Mantiene la miniatura activa sincronizada mientras el usuario desliza. */
  #onStageScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const center = this.stage.scrollLeft + this.stage.clientWidth / 2;
      const current = this.items
        .filter((item) => !item.hidden)
        .find((item) => item.offsetLeft <= center && item.offsetLeft + item.offsetWidth > center);
      if (current) this.#setActiveThumb(current.dataset.mediaId);
    });
  };

  #setActiveThumb(mediaId) {
    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
